import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api, STATUS_OPTIONS, UNIT_OPTIONS, PRODUCT_OPTIONS, todayISO } from "@/lib/lorryApi";

const emptyForm = {
  registration: "",
  product: "",
  load_amount: "",
  load_unit: "tons",
  driver_name: "",
  date: todayISO(),
  status: "pending",
  notes: "",
};

export default function LorryFormDialog({ open, onOpenChange, editing, onSaved, drivers = [], registrations = [] }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? { ...emptyForm, ...editing, load_amount: String(editing.load_amount ?? "") }
          : emptyForm
      );
    }
  }, [open, editing]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.registration.trim() || !form.product.trim() || !form.driver_name.trim()) {
      toast.error("Please fill registration, product and driver");
      return;
    }
    const amount = parseFloat(form.load_amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Load amount must be a positive number");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        registration: form.registration.trim().toUpperCase(),
        product: form.product.trim(),
        load_amount: amount,
        load_unit: form.load_unit,
        driver_name: form.driver_name.trim(),
        date: form.date,
        status: form.status,
        notes: form.notes || "",
      };
      if (editing?.id) {
        await api.put(`/lorries/${editing.id}`, payload);
        toast.success("Lorry entry updated");
      } else {
        await api.post("/lorries", payload);
        toast.success("Lorry entry added");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="lorry-form-dialog"
        className="bg-zinc-950 border border-zinc-800 rounded-none sm:max-w-[600px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <span className="overline text-yellow-500">
            {editing ? "Edit Entry" : "New Entry"}
          </span>
          <DialogTitle className="font-display text-2xl font-black tracking-tight text-zinc-100">
            {editing ? "Update Lorry Load" : "Register Lorry Load"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Enter the registration, driver and cargo details for this haul.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <Field label="Registration">
            <Input
              data-testid="input-registration"
              value={form.registration}
              onChange={(e) => update("registration", e.target.value)}
              placeholder="MH12 AB 1234"
              list="registration-suggestions"
              autoComplete="off"
              className="bg-zinc-900 border-zinc-800 rounded-sm uppercase tracking-wider focus-visible:ring-yellow-500"
            />
            <datalist id="registration-suggestions">
              {(registrations || []).map((r) => (
                <option key={r.plate} value={r.plate}>
                  {[
                    r.trips ? `${r.trips} trip${r.trips === 1 ? "" : "s"}` : "Saved",
                    r.last_product,
                    r.last_driver,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </option>
              ))}
            </datalist>
          </Field>

          <Field label="Driver Name">
            <Input
              data-testid="input-driver"
              value={form.driver_name}
              onChange={(e) => update("driver_name", e.target.value)}
              placeholder="e.g. Rajesh Kumar"
              list="driver-suggestions"
              autoComplete="off"
              className="bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500"
            />
            <datalist id="driver-suggestions">
              {(drivers || []).map((d) => (
                <option key={d.name} value={d.name}>
                  {d.trips ? `${d.trips} trip${d.trips === 1 ? "" : "s"}` : "Saved"}
                </option>
              ))}
            </datalist>
          </Field>

          <Field label="Product">
            <Input
              data-testid="input-product"
              value={form.product}
              onChange={(e) => update("product", e.target.value)}
              placeholder="e.g. Sharp Sand"
              list="product-suggestions"
              autoComplete="off"
              className="bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500"
            />
            <datalist id="product-suggestions">
              {PRODUCT_OPTIONS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Load Amount">
              <Input
                data-testid="input-amount"
                type="number"
                step="0.01"
                min="0"
                value={form.load_amount}
                onChange={(e) => update("load_amount", e.target.value)}
                placeholder="0.00"
                className="bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500"
              />
            </Field>
            <Field label="Unit">
              <Select value={form.load_unit} onValueChange={(v) => update("load_unit", v)}>
                <SelectTrigger
                  data-testid="select-unit"
                  className="bg-zinc-900 border-zinc-800 rounded-sm focus:ring-yellow-500"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
                  {UNIT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Date">
            <Input
              data-testid="input-date"
              type="date"
              value={form.date}
              onChange={(e) => update("date", e.target.value)}
              className="bg-zinc-900 border-zinc-800 rounded-sm focus-visible:ring-yellow-500"
            />
          </Field>

          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => update("status", v)}>
              <SelectTrigger
                data-testid="select-status"
                className="bg-zinc-900 border-zinc-800 rounded-sm focus:ring-yellow-500"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="sm:col-span-2">
            <Field label="Notes">
              <Textarea
                data-testid="input-notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                placeholder="Optional remarks..."
                rows={2}
                className="bg-zinc-900 border-zinc-800 rounded-sm resize-none focus-visible:ring-yellow-500"
              />
            </Field>
          </div>

          <DialogFooter className="sm:col-span-2 mt-2 gap-2">
            <Button
              type="button"
              variant="ghost"
              data-testid="button-cancel"
              onClick={() => onOpenChange(false)}
              className="rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="button-save"
              disabled={saving}
              className="rounded-sm bg-yellow-500 hover:bg-yellow-400 text-zinc-950 font-semibold tracking-wide active:scale-[0.98] transition-transform"
            >
              {saving ? "Saving..." : editing ? "Update Entry" : "Add Entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="overline text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}
